/**
 * IndexedDB persister for TanStack Query (Level-1 offline: read-only cached data).
 *
 * Security model (multi-tenant, potentially shared devices):
 *  - OPT-IN ONLY. A query is persisted to disk exclusively when it sets
 *    `meta: { persist: true }` AND has succeeded. Nothing is cached by default, so
 *    tokens / sensitive endpoints never land in IndexedDB unless a developer
 *    explicitly and deliberately opts a specific, safe query in.
 *  - The real tenant-isolation boundary is the logout wipe: BackendAuth.logout()
 *    calls clearPersistedQueryCache() (+ the SW drops its page/RSC caches), so a
 *    previous user's cached data cannot survive into the next session on a shared
 *    device. Do NOT rely on the store key for isolation.
 *  - `buster` = the build hash (NEXT_PUBLIC_SW_BUSTER): a new deploy invalidates
 *    the whole persisted cache, preventing stale-shape data after a schema change.
 *
 * NEVER opt in a query whose response embeds auth tokens, CSRF tokens, another
 * tenant's data, or PII that must not rest on disk.
 */
import { get, set, del, clear, createStore } from 'idb-keyval';

const DB_NAME = 'xoffi-query-cache';
const STORE_NAME = 'tanstack-query';
const CLIENT_KEY = 'client';

let idbStore = null;
function getStore() {
  if (typeof indexedDB === 'undefined') return null;
  if (!idbStore) idbStore = createStore(DB_NAME, STORE_NAME);
  return idbStore;
}

/** Persister implementing the TanStack Query Persister interface. */
export function createIDBPersister() {
  return {
    persistClient: async (client) => {
      const store = getStore();
      if (!store) return;
      try {
        await set(CLIENT_KEY, client, store);
      } catch {
        /* quota / serialization — non-fatal, offline cache is best-effort */
      }
    },
    restoreClient: async () => {
      const store = getStore();
      if (!store) return undefined;
      try {
        return await get(CLIENT_KEY, store);
      } catch {
        return undefined;
      }
    },
    removeClient: async () => {
      const store = getStore();
      if (!store) return;
      try {
        await del(CLIENT_KEY, store);
      } catch {
        /* ignore */
      }
    },
  };
}

/**
 * Wipe the entire persisted query cache. Called from BackendAuth.logout() and
 * defensively on login (auth change) so no cached tenant data leaks across
 * sessions. Safe to call when IndexedDB is unavailable.
 */
export async function clearPersistedQueryCache() {
  const store = getStore();
  if (!store) return;
  try {
    await clear(store);
  } catch {
    /* ignore */
  }
}

export const QUERY_PERSIST_MAX_AGE = 24 * 60 * 60 * 1000; // 24h
export const QUERY_PERSIST_BUSTER = process.env.NEXT_PUBLIC_SW_BUSTER || 'dev';

/** shouldDehydrateQuery: persist only explicitly-opted-in, successful queries. */
export function shouldPersistQuery(query) {
  return query?.meta?.persist === true && query?.state?.status === 'success';
}
